import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import CategoriaForm from './CategoriaForm';
import { categoriasService } from '../../services/categoriasService';
import { empresasService } from '../../services/empresasService';
import { sucursalesService } from '../../services/sucursalesService';
import SnowFallComponent from 'components/SnowFallComponent';

function Categorias() {
  const [categorias, setCategorias] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoriaToDelete, setCategoriaToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filtroSucursal, setFiltroSucursal] = useState('');
  const [buscarTexto, setBuscarTexto] = useState('');
  const [categoriasFiltradas, setCategoriasFiltradas] = useState([]);

  useEffect(() => {
    loadEmpresaYSucursales();
  }, []);

  useEffect(() => {
    if (!loadingSucursales) {
      loadCategorias();
    }
  }, [filtroSucursal, loadingSucursales]);

  useEffect(() => {
    // Filtrar categorías cuando cambia el texto de búsqueda
    if (buscarTexto.trim()) {
      const searchTerm = buscarTexto.toLowerCase().trim();
      const filtradas = categorias.filter(cat => {
        const nombre = (cat.Nombre || '').toLowerCase();
        const descripcion = (cat.Descripcion || '').toLowerCase();
        return nombre.includes(searchTerm) || descripcion.includes(searchTerm);
      });
      setCategoriasFiltradas(filtradas);
    } else {
      setCategoriasFiltradas(categorias);
    }
  }, [buscarTexto, categorias]);

  // Verificar si hay un ID en sessionStorage para abrir el detalle/edición automáticamente
  useEffect(() => {
    const openDetailId = sessionStorage.getItem('openDetail_categoria');
    if (openDetailId && !loading && empresa) {
      // Cargar la categoría específica desde la API para asegurar que esté disponible
      // independientemente del filtro de sucursal
      const loadCategoriaFromAPI = async () => {
        try {
          const response = await categoriasService.getById(openDetailId);
          if (response.success && response.data) {
            sessionStorage.removeItem('openDetail_categoria');
            handleEdit(response.data);
          } else {
            // Si no se encuentra en la API, intentar buscarla en la lista actual
            sessionStorage.removeItem('openDetail_categoria');
            const categoria = categorias.find(c => 
              (c.CategoriaID || c.categoriaID)?.toString() === openDetailId
            );
            if (categoria) {
              handleEdit(categoria);
            }
          }
        } catch (error) {
          // Si hay error, intentar buscarla en la lista actual como fallback
          sessionStorage.removeItem('openDetail_categoria');
          const categoria = categorias.find(c => 
            (c.CategoriaID || c.categoriaID)?.toString() === openDetailId
          );
          if (categoria) {
            handleEdit(categoria);
          }
        }
      };
      
      loadCategoriaFromAPI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, categorias, empresa]);

  const loadEmpresaYSucursales = async () => {
    try {
      setLoadingSucursales(true);
      // Obtener la empresa activa (solo hay una)
      const empresasResponse = await empresasService.getAll();
      if (empresasResponse.success && empresasResponse.data) {
        const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        if (empresaActiva) {
          setEmpresa(empresaActiva);
          // Cargar sucursales de la empresa
          const sucursalesResponse = await sucursalesService.getAll(empresaActiva.EmpresaID || empresaActiva.empresaID);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            const normalized = sucursalesData.map(suc => ({
              SucursalID: suc.SucursalID || suc.sucursalID,
              Nombre: suc.Nombre || suc.nombre || '',
            }));
            setSucursales(normalized);
            // Seleccionar la primera sucursal por defecto si hay sucursales
            if (normalized.length > 0) {
              setFiltroSucursal(normalized[0].SucursalID.toString());
            }
          }
        }
      }
    } catch (err) {
      setError('Error al cargar datos de la empresa');
    } finally {
      setLoadingSucursales(false);
    }
  };

  const loadCategorias = async () => {
    try {
      setLoading(true);
      setError('');
      const empresaId = empresa ? (empresa.EmpresaID || empresa.empresaID) : null;
      const sucursalId = filtroSucursal ? parseInt(filtroSucursal) : null;
      const response = await categoriasService.getAll(empresaId, sucursalId);
      if (response.success && response.data) {
        const categoriasData = Array.isArray(response.data) ? response.data : [];
        // Normalizar los datos para manejar tanto camelCase como PascalCase
        const normalizedData = categoriasData.map(cat => ({
          CategoriaID: cat.CategoriaID || cat.categoriaID,
          EmpresaID: cat.EmpresaID || cat.empresaID,
          SucursalID: cat.SucursalID || cat.sucursalID,
          Nombre: cat.Nombre || cat.nombre || '',
          Descripcion: cat.Descripcion || cat.descripcion,
          Activo: cat.Activo !== undefined ? cat.Activo : (cat.activo !== undefined ? cat.activo : true),
        }));
        setCategorias(normalizedData);
        // Inicializar categorías filtradas con todas las categorías
        setCategoriasFiltradas(normalizedData);
      } else {
        setError(response.Message || response.message || 'Error al cargar categorías');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCategoria(null);
    setShowForm(true);
  };

  const handleEdit = (categoria) => {
    setEditingCategoria(categoria);
    setShowForm(true);
  };

  const handleDelete = (categoria) => {
    setCategoriaToDelete(categoria);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!categoriaToDelete) return;

    try {
      setDeleting(true);
      const categoriaId = categoriaToDelete.CategoriaID || categoriaToDelete.categoriaID;
      const response = await categoriasService.delete(categoriaId);
      if (response.success) {
        setSuccess('Categoría desactivada exitosamente');
        setShowDeleteModal(false);
        setCategoriaToDelete(null);
        loadCategorias();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.Message || response.message || 'Error al desactivar categoría');
      }
    } catch (err) {
      setError(err.message || 'Error de conexión al desactivar categoría');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingCategoria(null);
    setSuccess(editingCategoria ? 'Categoría actualizada exitosamente' : 'Categoría creada exitosamente');
    loadCategorias();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCategoria(null);
  };

  const getSucursalNombre = (sucursalId) => {
    if (!sucursalId) return 'Todas las sucursales';
    const sucursal = sucursales.find(s => (s.SucursalID || s.sucursalID) === sucursalId);
    return sucursal ? (sucursal.Nombre || sucursal.nombre) : '-';
  };

  const columns = [
    {
      key: 'sucursal',
      label: 'Sucursal',
      accessor: 'SucursalID',
      render: (value) => getSucursalNombre(value),
    },
    {
      key: 'nombre',
      label: 'Nombre',
      accessor: 'Nombre',
    },
    {
      key: 'descripcion',
      label: 'Descripción',
      accessor: 'Descripcion',
      render: (value) => value || '-',
    },
    {
      key: 'activo',
      label: 'Estado',
      accessor: 'Activo',
      render: (value) => (
        <Badge color={value ? 'success' : 'secondary'}>
          {value ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => (
        <div>
          <Button
            color="info"
            size="sm"
            className="mr-2"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
          >
            <i className="tim-icons icon-pencil" />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
          >
            <i className="tim-icons icon-simple-remove" />
          </Button>
        </div>
      ),
    },
  ];

  if (showForm) {
    return (
      <div className="content">
        <SnowFallComponent />
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">
                  {editingCategoria ? 'Editar Categoría' : 'Nueva Categoría'}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <CategoriaForm
                  categoria={editingCategoria}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Categorías</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button color="primary" onClick={handleCreate}>
                    <i className="tim-icons icon-simple-add" /> Nueva Categoría
                  </Button>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <select
                    className="form-control"
                    value={filtroSucursal}
                    onChange={(e) => setFiltroSucursal(e.target.value)}
                    disabled={loadingSucursales}
                  >
                    <option value="">Todas las sucursales</option>
                    {sucursales.map((suc) => (
                      <option key={suc.SucursalID} value={suc.SucursalID}>
                        {suc.Nombre}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col md="8">
                  <InputGroup>
                    <InputGroupText>
                      <i className="tim-icons icon-zoom-split" />
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Buscar por nombre o descripción..."
                      value={buscarTexto}
                      onChange={(e) => setBuscarTexto(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={categoriasFiltradas} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setCategoriaToDelete(null);
        }}
        title="Desactivar Categoría"
        message={`¿Está seguro de desactivar la categoría "${categoriaToDelete?.Nombre || categoriaToDelete?.nombre}"?`}
        confirmText="Desactivar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
}

export default Categorias;

